import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/i18n/i18n";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, Languages, Mail, Loader2, Link2, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { t, lang, setLang } = useI18n();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  // Which credential the user has requested. Null = not yet sent; "code" =
  // showed code entry input; "link" = showed "check your email" note.
  const [sentMethod, setSentMethod] = useState(null);
  const [blockedMsg, setBlockedMsg] = useState("");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  const requestCredential = async (method) => {
    if (!email) return;
    try {
      setSending(true);
      const r = await api.post("/auth/magic-link/request", { email, method });
      if (r?.data?.throttled) {
        toast.warning(
          "Ya solicitaste varios accesos recientemente. Espera unos minutos antes de intentar de nuevo.",
        );
        return;
      }
      setSentMethod(method);
      toast.success(method === "code" ? t("login_code_check_email") : t("magic_link_sent"));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403 && typeof detail === "string") {
        setBlockedMsg(detail);
        toast.error(detail);
      } else {
        toast.error(t("error_generic"));
      }
    } finally {      setSending(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    const clean = (code || "").replace(/\s|-/g, "");
    if (clean.length !== 6) {
      toast.error(t("login_code_must_be_6"));
      return;
    }
    try {
      setVerifying(true);
      await api.post("/auth/magic-link/verify-code", { email, code: clean });
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("login_invalid_code"));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-white">
      {/* Left side - form */}
      <div className="flex flex-col p-8 lg:p-12">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 bg-brand-blue text-white rounded-md flex items-center justify-center">
              <GraduationCap className="h-5 w-5" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">{t("app_name")}</span>
          </div>
          <button
            data-testid="lang-toggle-button"
            onClick={() => setLang(lang === "es" ? "en" : "es")}
            className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900 transition-colors"
          >
            <Languages className="h-4 w-4" />
            {lang === "es" ? "EN" : "ES"}
          </button>
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
          <img
            src="https://customer-assets.emergentagent.com/job_escuela-reports/artifacts/vmevlka0_Screenshot%202026-03-11%20at%206.06.04%E2%80%AFPM.png"
            alt="We Speak Spanish"
            className="h-28 sm:h-32 mb-6 object-contain self-start"
          />
          <div className="mb-2 inline-flex">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-brand border border-orange-100 bg-orange-50 px-2 py-1 rounded">
              {t("tagline")}
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 leading-[1.05] mb-3">
            {t("login_title")}
          </h1>
          <p className="text-slate-600 text-base mb-10">{t("login_subtitle")}</p>

          <form onSubmit={(e) => { e.preventDefault(); requestCredential("code"); }} className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">{t("email")}</label>
              <Input
                type="email"
                data-testid="magic-email-input"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setBlockedMsg(""); }}
                required
                disabled={!!sentMethod}
                className="h-11"
              />
              {!sentMethod && (
                <p className="text-[11px] text-slate-500 mt-1.5">
                  {t("magic_link_hint") || "Usa el correo con el que fuiste registrado en la plataforma."}
                </p>
              )}
            </div>

            {blockedMsg && (
              <div
                className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 space-y-2"
                data-testid="access-blocked-message"
              >
                <p data-testid="access-blocked-detail">{blockedMsg}</p>
                <p>
                  Si creés que es un error, escribí a{" "}
                  <a href="mailto:admin@wespeakspanish-cr.com" className="font-semibold underline" data-testid="blocked-email-link">
                    admin@wespeakspanish-cr.com
                  </a>{" "}
                  o a Wagner Freer por WhatsApp.
                </p>
                <a
                  href="https://wa.me/50670768283?text=Hola%20Wagner%2C%20mi%20acceso%20a%20la%20plataforma%20est%C3%A1%20bloqueado."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-xs font-semibold transition-colors"
                  data-testid="blocked-whatsapp-link"
                >
                  WhatsApp (506) 7076-8283
                </a>
              </div>
            )}

            {/* Step 1: no email sent yet — show CODE primary + magic-link alt */}
            {!sentMethod && !blockedMsg && (
              <>
                <Button
                  type="submit"
                  data-testid="request-code-button"
                  disabled={sending || !email}
                  size="lg"
                  className="w-full rounded-md h-12 text-sm font-semibold tracking-wide text-white shadow-md hover:shadow-lg transition-shadow"
                  style={{ background: "linear-gradient(90deg, #1d4ed8 0%, #3b82f6 100%)" }}
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  {t("login_receive_code_btn")}
                </Button>

                <div className="flex items-center gap-3 pt-2 text-[11px] uppercase tracking-widest text-slate-400">
                  <div className="h-px bg-slate-200 flex-1" />
                  {t("login_or_other_method")}
                  <div className="h-px bg-slate-200 flex-1" />
                </div>

                <button
                  type="button"
                  data-testid="request-link-button"
                  disabled={sending || !email}
                  onClick={() => requestCredential("link")}
                  className="w-full h-11 flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Link2 className="h-4 w-4" />
                  {t("login_magic_link_btn")}
                </button>
              </>
            )}

            {/* Step 2a: code was requested — show 6-digit input */}
            {sentMethod === "code" && (
              <div className="space-y-3">
                <p className="text-[13px] text-slate-600 leading-relaxed">
                  {t("login_code_sent_intro")} <strong className="text-slate-900">{email}</strong>.
                  {" "}{t("login_code_sent_hint")}
                </p>
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  data-testid="code-input"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  maxLength={6}
                  autoFocus
                  className="h-14 text-center text-2xl font-mono tracking-[0.5em] font-bold"
                />
                <Button
                  type="button"
                  data-testid="verify-code-button"
                  disabled={verifying || code.length !== 6}
                  onClick={submitCode}
                  size="lg"
                  className="w-full btn-brand rounded-md h-12 text-sm font-semibold tracking-wide"
                >
                  {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                  {t("login_submit_code_btn")}
                </Button>
                <div className="text-center text-xs text-slate-500 pt-1">
                  {t("login_code_not_received")}{" "}
                  <button
                    type="button"
                    onClick={() => { setCode(""); setSentMethod(null); }}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    {t("login_retry")}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2b: magic link was requested — show info */}
            {sentMethod === "link" && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 space-y-2">
                <p className="font-semibold">{t("login_check_your_email")}</p>
                <p className="text-emerald-800 text-[13px] leading-relaxed">
                  {t("login_link_sent_intro")} <strong>{email}</strong>. {t("login_link_sent_hint")}
                </p>
                <button
                  type="button"
                  onClick={() => setSentMethod(null)}
                  className="text-emerald-700 hover:underline text-xs font-medium"
                >
                  {t("login_back")}
                </button>
              </div>
            )}
          </form>

          <p className="text-xs text-slate-500 mt-6 text-center">
            {t("login_subtitle")} • {t("app_name")}
          </p>
        </div>

        <p className="text-xs text-slate-400">© {new Date().getFullYear()} {t("app_name")}</p>{/* tz-ok: solo el año del pie */}
      </div>

      {/* Right side - image */}
      <div className="hidden lg:block relative overflow-hidden bg-brand-blue">
        <img
          src="https://customer-assets.emergentagent.com/job_escuela-reports/artifacts/e8tmmnla_Atenas-Group.png"
          alt="We Speak Spanish — Atenas group"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0" style={{background: "linear-gradient(to top right, rgba(0,74,173,0.85), rgba(0,74,173,0.4), transparent)"}} />
        <div className="relative h-full p-12 flex flex-col justify-end text-white">
          <div className="max-w-md">
            <span className="text-xs uppercase tracking-[0.3em] text-orange-300 font-semibold">{t("dashboard")}</span>
            <h2 className="font-display text-4xl font-bold leading-tight mt-2">
              {t("tagline")}
            </h2>
            <p className="text-slate-300 mt-3 text-sm">
              {t("attendance")} • {t("journal")} • {t("payments")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
